import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { getSupabaseClient } from '@/lib/supabase';
import type { User, Session } from '@supabase/supabase-js';
import { normalizeError } from '@/lib/error-normalizer';
import { normalizePermission } from '@/utils/rbac-normalize';
import { isTurnstileEnabled } from '@/utils/turnstile-config';
import type {
  Person,
  TenantMembership,
  Role,
  Permission,
  RoleAssignment,
  FirstLoginState,
  LegalAcceptance,
} from '@/types/auth';
import { dispatcherMaster, type DispatchContext } from '@/core/dispatcher';
import { generateCorrelationId } from '@/core/execution/ExecutionContract';

interface AuthContextType {
  user: User | null;
  person: Person | null;
  tenantMemberships: TenantMembership[];
  currentTenantId: string | null;
  tenantIds: string[];
  tenants: { id: string; name: string }[];
  roles: Role[];
  permissions: Permission[];
  roleAssignments: RoleAssignment[];
  firstLoginState: FirstLoginState | null;
  legalAcceptances: LegalAcceptance[];
  isAdminMaster: boolean;
  isCandidate: boolean;
  isEmpresa: boolean;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (
    email: string,
    password: string,
    options?: { turnstileToken?: string },
  ) => Promise<{ error?: string }>;
  loginWithProvider: (
    provider: 'google' | 'azure',
  ) => Promise<{ error?: string }>;
  logout: () => Promise<void>;
  register: (
    email: string,
    password: string,
    profileData: {
      full_name: string;
      email: string;
      phone?: string;
      tenantId?: string;
      roleId?: string;
      turnstileToken?: string;
      emailRedirectTo?: string;
      signupContext?: 'candidato' | 'empresa';
      companyName?: string;
      cnpj?: string;
    },
  ) => Promise<{ error?: string; status?: 'success' | 'email_pending' }>;
  resetPassword: (email: string) => Promise<{ error?: string }>;
  updateProfile: (data: Partial<Person>) => Promise<{ error?: string }>;
  changePassword: (
    currentPassword: string,
    newPassword: string,
    turnstileToken?: string,
  ) => Promise<{ error?: string; destination?: string }>;
  acceptTerms: (
    documentType: string,
    documentVersion: string,
    metadata?: Record<string, unknown>,
  ) => Promise<{ error?: string }>;
  updateFirstLoginState: (
    updates: Partial<FirstLoginState>,
  ) => Promise<{ error?: string }>;
  hasPermission: (permissionKey: string) => boolean;
  hasAnyPermission: (permissionKeys: string[]) => boolean;
  hasAllPermissions: (permissionKeys: string[]) => boolean;
  switchTenant: (tenantId: string | null) => Promise<void>;
  refreshAuthData: (authUserId?: string) => Promise<void>;
  resolvePostLoginDestination: (overrideWelcomeCompleted?: boolean) => string;
  authError: string | null;
  recoveryMode: boolean;
}

const SESSION_PERSIST_KEY = 'jst_session_persist';
const TENANT_SELECTION_STORAGE_PREFIX = 'jst_selected_tenant:';

interface AuthDataLoadResult {
  operationId: number;
  currentTenantId: string | null;
}

function getTenantSelectionStorageKey(authUserId: string): string {
  return `${TENANT_SELECTION_STORAGE_PREFIX}${authUserId}`;
}

function readTenantSelection(authUserId?: string): string | null {
  if (!authUserId) return null;

  try {
    return sessionStorage.getItem(getTenantSelectionStorageKey(authUserId));
  } catch (error) {
    console.warn(
      '[AUTH:tenant] Não foi possível ler a preferência de tenant da sessão.',
      error,
    );
    return null;
  }
}

function persistTenantSelection(
  authUserId: string,
  tenantId: string | null,
): void {
  try {
    const key = getTenantSelectionStorageKey(authUserId);
    if (tenantId) {
      sessionStorage.setItem(key, tenantId);
    } else {
      sessionStorage.removeItem(key);
    }
  } catch (error) {
    console.warn(
      '[AUTH:tenant] Não foi possível persistir a preferência de tenant.',
      error,
    );
  }
}

function clearSessionPersistence() {
  try {
    sessionStorage.removeItem(SESSION_PERSIST_KEY);
  } catch {
    // ignore
  }
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [person, setPerson] = useState<Person | null>(null);
  const [tenantMemberships, setTenantMemberships] = useState<
    TenantMembership[]
  >([]);
  const [currentTenantId, setCurrentTenantId] = useState<string | null>(null);
  const [tenants, setTenants] = useState<{ id: string; name: string }[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [roleAssignments, setRoleAssignments] = useState<RoleAssignment[]>([]);
  const [firstLoginState, setFirstLoginState] =
    useState<FirstLoginState | null>(null);
  const [legalAcceptances, setLegalAcceptances] = useState<LegalAcceptance[]>(
    [],
  );
  const [isAdminMaster, setIsAdminMaster] = useState(false);
  const [isCandidate, setIsCandidate] = useState(false);
  const [isEmpresa, setIsEmpresa] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [session, setSession] = useState<Session | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const [recoveryMode, setRecoveryMode] = useState(false);
  const initialSessionProcessedRef = useRef(false);
  const isMountedRef = useRef(true);
  const authLoadInFlightRef = useRef(false);
  const loadOperationIdRef = useRef(0);

  const loadAuthData = useCallback(
    async (
      authUserId?: string,
      explicitTenantId?: string | null,
    ): Promise<AuthDataLoadResult> => {
      const currentOperationId = ++loadOperationIdRef.current;
      const requestedTenantId =
        explicitTenantId === undefined
          ? readTenantSelection(authUserId)
          : explicitTenantId;

      try {
        const supabase = getSupabaseClient();
        if (!supabase || !isMountedRef.current) {
          return {
            operationId: currentOperationId,
            currentTenantId: null,
          };
        }

        if (!authUserId) {
          throw new Error('Não foi possível identificar a sessão autenticada.');
        }

        console.log('[AUTH:IDENTITY] loadAuthData start', {
          authUserId,
          operationId: currentOperationId,
        });

        const { data: personData, error: personError } = await supabase
          .from('people')
          .select('*')
          .eq('auth_user_id', authUserId)
          .maybeSingle();

        console.log('[AUTH:IDENTITY] people loaded', {
          hasPerson: !!personData,
          error: personError?.message ?? null,
        });

        if (personError) throw personError;
        if (!personData) {
          throw new Error(
            'Identidade não encontrada. Verifique se seu cadastro está completo ou solicite acesso ao administrador.',
          );
        }

        const { data: membershipData, error: membershipError } = await supabase
          .from('tenant_memberships')
          .select('*')
          .eq('person_id', personData.id)
          .eq('status', 'active')
          .order('tenant_id', { ascending: true });

        if (membershipError) throw membershipError;

        console.log('[AUTH:IDENTITY] memberships loaded', {
          count: (membershipData || []).length,
        });

        const tenantIds = Array.from(
          new Set(
            (membershipData || [])
              .map((m: TenantMembership) => m.tenant_id)
              .filter((id): id is string => Boolean(id)),
          ),
        );
        const validatedTenantId =
          requestedTenantId && tenantIds.includes(requestedTenantId)
            ? requestedTenantId
            : null;

        if (requestedTenantId && !validatedTenantId) {
          persistTenantSelection(authUserId, null);
        }

        let tenantsData: { id: string; name: string }[] = [];
        if (tenantIds.length > 0) {
          const { data: tenantsResult, error: tenantsError } = await supabase
            .from('tenants')
            .select('id, name')
            .in('id', tenantIds);
          if (tenantsError) throw tenantsError;
          tenantsData = (tenantsResult || []) as {
            id: string;
            name: string;
          }[];
        }

        console.log('[AUTH:IDENTITY] tenants loaded', {
          count: tenantsData.length,
        });

        // Fetch all role_assignments, then filter by tenant and expiration
        const { data: roleAssignmentData, error: roleAssignmentError } =
          await supabase
            .from('role_assignments')
            .select('*')
            .eq('person_id', personData.id);

        console.log('[AUTH:IDENTITY] role_assignments loaded', {
          count: (roleAssignmentData || []).length,
          error: roleAssignmentError?.message ?? null,
        });

        if (roleAssignmentError) throw roleAssignmentError;

        // Filter assignments to global roles and the explicitly validated tenant.
        const now = new Date().toISOString();
        const validAssignments = (roleAssignmentData || []).filter(
          (ra: RoleAssignment) => {
            if (ra.expires_at && ra.expires_at < now) {
              return false;
            }
            return ra.tenant_id === null || ra.tenant_id === validatedTenantId;
          },
        );

        const roleIds = Array.from(
          new Set(
            validAssignments
              .map((ra: RoleAssignment) => ra.role_id)
              .filter(Boolean),
          ),
        );

        console.log('[AUTH:IDENTITY] roleIds resolved', { roleIds });

        const { data: rolesData, error: rolesError } =
          roleIds.length > 0
            ? await supabase.from('roles').select('*').in('id', roleIds)
            : { data: [], error: null };
        if (rolesError) throw rolesError;

        // Separar funções pelo escopo real do banco (coluna scope, não is_global).
        const globalRoles = (rolesData || []).filter(
          (role: Role) => role.scope === 'global',
        );

        const tenantRoles = (rolesData || []).filter(
          (role: Role) => role.scope === 'tenant',
        );

        const globalRoleIds = new Set(globalRoles.map((role) => role.id));

        const validGlobalAssignments = validAssignments.filter(
          (assignment) =>
            assignment.tenant_id === null &&
            globalRoleIds.has(assignment.role_id),
        );

        const tenantRoleIds = new Set(tenantRoles.map((role) => role.id));

        const validTenantAssignments = validAssignments.filter(
          (assignment) =>
            assignment.tenant_id === validatedTenantId &&
            tenantRoleIds.has(assignment.role_id),
        );

        // Combine validated role IDs
        const validatedRoleIds = Array.from(
          new Set([
            ...validGlobalAssignments.map((assignment) => assignment.role_id),
            ...validTenantAssignments.map((assignment) => assignment.role_id),
          ]),
        ).filter((id): id is string => Boolean(id));

        // Re-filter rolesData to only validated roles
        const validatedRoles = (rolesData || []).filter((role) =>
          validatedRoleIds.includes(role.id),
        );

        const adminMaster = validatedRoles.some(
          (role: Role) =>
            role.scope === 'global' &&
            (role.name === 'admin_master' || role.slug === 'admin_master'),
        );

        console.log('[AUTH:IDENTITY] roles loaded', {
          count: validatedRoles.length,
          names: validatedRoles.map((r: Role) => r.name),
          scopes: validatedRoles.map((r: Role) => r.scope),
          adminMaster,
        });

        const isCandidate = validatedRoles.some(
          (r: Role) => r.name === 'candidato',
        );

        const isEmpresa = validatedRoles.some(
          (r: Role) => r.name === 'company_representative',
        );

        let permissionsData: Permission[] = [];
        if (validatedRoleIds.length > 0) {
          const { data: rolePerms, error: rolePermissionsError } =
            await supabase
              .from('role_permissions')
              .select('permission_id')
              .in('role_id', validatedRoleIds);
          if (rolePermissionsError) throw rolePermissionsError;

          const permissionIds = Array.from(
            new Set(
              (rolePerms || [])
                .map((rp: { permission_id: string }) => rp.permission_id)
                .filter(Boolean),
            ),
          );

          // admin_master global recebe TODAS as permissões do banco,
          const allPermissionIds: string[] = permissionIds;

          if (allPermissionIds.length > 0) {
            const { data: perms, error: permissionsError } = await supabase
              .from('permissions')
              .select('*')
              .in('id', allPermissionIds);
            if (permissionsError) throw permissionsError;

            const normalizedPermissions = (perms || [])
              .map(normalizePermission)
              .filter((p): p is Permission => p !== null);

            permissionsData = normalizedPermissions;
          }
        }

        console.log('[AUTH:IDENTITY] permissions loaded', {
          count: permissionsData.length,
          slugs: permissionsData.map((p) => p.name),
        });

        console.log('[AUTH:IDENTITY] candidate flags resolved', {
          isCandidate,
          isEmpresa,
          adminMaster,
          roles: validatedRoles.map((r: Role) => ({
            name: r.name,
            scope: r.scope,
          })),
        });

        const { data: firstLoginData } = await supabase
          .from('first_login_state')
          .select('*')
          .eq('person_id', personData.id)
          .maybeSingle();

        console.log('[AUTH:FIRST_LOGIN] first_login_state loaded', {
          personId: personData.id,
          firstLoginData: firstLoginData
            ? {
                welcome_completed_at: firstLoginData.welcome_completed_at,
                first_login_completed: firstLoginData.first_login_completed,
                must_change_password: firstLoginData.must_change_password,
                terms_version: firstLoginData.terms_version,
              }
            : null,
        });

        const { data: legalAcceptancesData } = await supabase
          .from('legal_acceptances')
          .select('*')
          .eq('person_id', personData.id)
          .order('accepted_at', { ascending: false });

        // Check if this operation is still current before updating state
        if (
          !isMountedRef.current ||
          currentOperationId !== loadOperationIdRef.current
        ) {
          console.log('[AUTH:IDENTITY] Stale operation discarded', {
            operationId: currentOperationId,
          });
          return {
            operationId: currentOperationId,
            currentTenantId: validatedTenantId,
          };
        }

        setPerson(personData as Person);
        setTenantMemberships((membershipData || []) as TenantMembership[]);
        setCurrentTenantId(validatedTenantId);
        setTenants(tenantsData);
        setRoles(validatedRoles as Role[]);
        setPermissions(permissionsData);
        // Store only the validated assignments for audit
        setRoleAssignments([
          ...validGlobalAssignments,
          ...validTenantAssignments,
        ] as RoleAssignment[]);
        setFirstLoginState((firstLoginData as FirstLoginState) || null);
        setLegalAcceptances((legalAcceptancesData || []) as LegalAcceptance[]);
        setIsAdminMaster(adminMaster);
        setIsCandidate(isCandidate);
        setIsEmpresa(isEmpresa);

        console.log('[AUTH:IDENTITY] loadAuthData complete', {
          personId: personData.id,
          currentTenantId: validatedTenantId,
          isCandidate,
          isAdminMaster: adminMaster,
          permissionCount: permissionsData.length,
          operationId: currentOperationId,
        });
        return {
          operationId: currentOperationId,
          currentTenantId: validatedTenantId,
        };
      } catch (error) {
        if (
          isMountedRef.current &&
          currentOperationId === loadOperationIdRef.current
        ) {
          console.error('[AUTH:IDENTITY] loadAuthData failed', error);
          if (explicitTenantId !== undefined) {
            setCurrentTenantId(null);
            setRoles([]);
            setPermissions([]);
            setRoleAssignments([]);
            setIsAdminMaster(false);
            setIsCandidate(false);
            setIsEmpresa(false);
          } else {
            setPerson(null);
            setTenantMemberships([]);
            setCurrentTenantId(null);
            setRoles([]);
            setPermissions([]);
            setRoleAssignments([]);
            setFirstLoginState(null);
            setLegalAcceptances([]);
            setIsAdminMaster(false);
            setIsCandidate(false);
            setIsEmpresa(false);
          }
        }
        throw error;
      }
    },
    [],
  );

  useEffect(() => {
    isMountedRef.current = true;
    const initAuth = async () => {
      try {
        const supabase = getSupabaseClient();

        if (!supabase) {
          if (isMountedRef.current) {
            setAuthError(
              normalizeError(
                new Error(
                  'Supabase não configurado. Defina VITE_SUPABASE_URL e VITE_SUPABASE_PUBLISHABLE_KEY.',
                ),
              ).userMessage,
            );
            setIsLoading(false);
          }
          return;
        }

        const {
          data: { session: initialSession },
        } = await supabase.auth.getSession();

        console.log('[AUTH] initAuth session:', {
          hasSession: !!initialSession,
          userId: initialSession?.user?.id ?? null,
        });

        if (!isMountedRef.current) return;

        const currentSession = initialSession;
        setSession(currentSession);
        setUser(currentSession?.user ?? null);

        if (currentSession?.user) {
          console.log('[AUTH] initAuth loading identity');
          authLoadInFlightRef.current = true;
          try {
            await loadAuthData(currentSession.user.id);
            initialSessionProcessedRef.current = true;
          } finally {
            authLoadInFlightRef.current = false;
          }
        }
      } catch (error) {
        console.error('Erro ao inicializar auth:', error);
      } finally {
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      }
    };

    initAuth();

    const supabase = getSupabaseClient();
    if (!supabase) {
      return () => {
        isMountedRef.current = false;
      };
    }

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      if (!isMountedRef.current) return;

      setSession(newSession);
      setUser(newSession?.user ?? null);
      setAuthError(null);

      const authEvent = event as string;

      if (authEvent === 'PASSWORD_RECOVERY') {
        console.log('[AUTH] password recovery detected');
        setRecoveryMode(true);
        initialSessionProcessedRef.current = false;
        authLoadInFlightRef.current = false;
        if (newSession?.user && isMountedRef.current) {
          try {
            await loadAuthData(newSession.user.id);
          } catch (identityError) {
            console.error(
              '[AUTH] identity incomplete on recovery',
              identityError,
            );
            await supabase.auth.signOut();
            if (isMountedRef.current) {
              setPerson(null);
              setTenantMemberships([]);
              setCurrentTenantId(null);
              setRoles([]);
              setPermissions([]);
              setRoleAssignments([]);
              setFirstLoginState(null);
              setLegalAcceptances([]);
              setIsAdminMaster(false);
              setIsCandidate(false);
              setIsEmpresa(false);
              setSession(null);
              setUser(null);
              setRecoveryMode(false);
            }
          }
        }
      } else if (
        authEvent === 'SIGNED_IN' &&
        newSession?.user &&
        !initialSessionProcessedRef.current &&
        !authLoadInFlightRef.current
      ) {
        authLoadInFlightRef.current = true;
        try {
          await new Promise((resolve) => setTimeout(resolve, 50));
          const { data: sessionData } = await supabase.auth.getSession();
          if (!sessionData.session) {
            throw new Error('Sessao invalida na recuperacao.');
          }
          await loadAuthData(newSession.user.id);
          initialSessionProcessedRef.current = true;
        } catch (identityError) {
          console.error(
            '[AUTH] identity incomplete on session restore',
            identityError,
          );
          initialSessionProcessedRef.current = false;
          await supabase.auth.signOut();
          if (isMountedRef.current) {
            setPerson(null);
            setTenantMemberships([]);
            setCurrentTenantId(null);
            setRoles([]);
            setPermissions([]);
            setRoleAssignments([]);
            setFirstLoginState(null);
            setLegalAcceptances([]);
            setIsAdminMaster(false);
            setSession(null);
            setUser(null);
          }
        } finally {
          authLoadInFlightRef.current = false;
        }
      } else if (
        authEvent === 'SIGNED_OUT' ||
        authEvent === 'SESSION_EXPIRED' ||
        authEvent === 'TOKEN_REFRESH_FAILED'
      ) {
        console.log('[AUTH] onAuthStateChange clear state:', authEvent);
        initialSessionProcessedRef.current = false;
        authLoadInFlightRef.current = false;
        setRecoveryMode(false);
        if (isMountedRef.current) {
          setPerson(null);
          setTenantMemberships([]);
          setCurrentTenantId(null);
          setRoles([]);
          setPermissions([]);
          setRoleAssignments([]);
          setFirstLoginState(null);
          setLegalAcceptances([]);
          setIsAdminMaster(false);
          setIsCandidate(false);
          setIsEmpresa(false);
          setSession(null);
          setUser(null);
        }
        if (
          authEvent === 'SESSION_EXPIRED' ||
          authEvent === 'TOKEN_REFRESH_FAILED'
        ) {
          setAuthError('Sessão expirada. Faça login novamente.');
        }
      }
    });

    return () => {
      isMountedRef.current = false;
      subscription.unsubscribe();
    };
  }, [loadAuthData]);

  const verifyTurnstileToken = async (
    token?: string,
  ): Promise<{ ok: boolean; error?: string }> => {
    if (isTurnstileEnabled() && !token) {
      return {
        ok: false,
        error: 'Conclua a verificação de segurança.',
      };
    }

    if (!token) {
      return { ok: true };
    }

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    if (!supabaseUrl) {
      return { ok: false, error: 'Supabase não configurado.' };
    }

    const siteverifyUrl = `${supabaseUrl.replace(/\/$/, '')}/functions/v1/turnstile-siteverify`;

    try {
      const response = await fetch(siteverifyUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });

      if (!response.ok) {
        return { ok: false, error: 'Falha ao verificar CAPTCHA.' };
      }

      const result = (await response.json()) as {
        success?: boolean;
        error?: string;
      };

      if (!result.success) {
        return {
          ok: false,
          error: 'Verificação de CAPTCHA inválida. Tente novamente.',
        };
      }

      return { ok: true };
    } catch (err) {
      console.error('[AUTH:TURNSTILE] siteverify failed', err);
      return { ok: false, error: 'Falha ao verificar CAPTCHA.' };
    }
  };

  const login = async (
    email: string,
    password: string,
    options: { turnstileToken?: string } = {},
  ): Promise<{ error?: string }> => {
    setAuthError(null);
    setIsLoading(true);
    initialSessionProcessedRef.current = false;
    authLoadInFlightRef.current = false;

    const supabase = getSupabaseClient();

    console.log('[AUTH:DIAGNOSTIC]', {
      hasSupabaseClient: !!supabase,
      hasUrl: !!import.meta.env?.VITE_SUPABASE_URL,
      hasKey: !!import.meta.env?.VITE_SUPABASE_PUBLISHABLE_KEY,
    });

    if (!supabase) {
      setIsLoading(false);
      return {
        error: normalizeError(
          new Error(
            'Supabase não configurado. Contate o administrador ou verifique as variáveis de ambiente.',
          ),
        ).userMessage,
      };
    }

    try {
      console.log('[AUTH:LOGIN] start', { email });

      if (isTurnstileEnabled() && !options.turnstileToken) {
        setIsLoading(false);
        return { error: 'Conclua a verificação de segurança.' };
      }

      if (options.turnstileToken) {
        const turnstileResult = await verifyTurnstileToken(
          options.turnstileToken,
        );
        if (!turnstileResult.ok) {
          setIsLoading(false);
          return { error: turnstileResult.error };
        }
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      console.log('[AUTH:LOGIN] signIn result', {
        hasUser: !!data?.user,
        hasSession: !!data?.session,
        error: error?.message ?? null,
      });

      if (error) {
        console.error('[AUTH:LOGIN] signIn failed', {
          code: (error as any)?.code || null,
          message: error?.message || null,
          status: (error as any)?.status || null,
          name: (error as any)?.name || null,
        });
        setIsLoading(false);
        return { error: normalizeError(error).userMessage };
      }

      if (data.user) {
        console.log('[AUTH:LOGIN] success — loading identity');
        setSession(data.session);
        setUser(data.user);

        try {
          await new Promise((resolve) => setTimeout(resolve, 50));
          const { data: sessionData } = await supabase.auth.getSession();
          if (!sessionData.session) {
            throw new Error('Sessao invalida apos login.');
          }

          // O onAuthStateChange (SIGNED_IN) já dispara loadAuthData.
          // Evita chamada duplicada: se a sessão já foi processada pelo listener,
          // não carregamos a identidade novamente.
          if (!initialSessionProcessedRef.current) {
            await loadAuthData(data.user.id);
          } else {
            console.log(
              '[AUTH:LOGIN] identidade já carregada pelo onAuthStateChange — pulando carga duplicada.',
            );
          }
        } catch (identityError) {
          console.error('[AUTH:LOGIN] identity incomplete', identityError);
          authLoadInFlightRef.current = false;
          await supabase.auth.signOut();
          setSession(null);
          setUser(null);
          setPerson(null);
          setTenantMemberships([]);
          setCurrentTenantId(null);
          setRoles([]);
          setPermissions([]);
          setRoleAssignments([]);
          setFirstLoginState(null);
          setLegalAcceptances([]);
          setIsAdminMaster(false);
          setIsCandidate(false);
          setIsEmpresa(false);
          return {
            error:
              'Identidade nao encontrada. Verifique se seu cadastro esta completo ou solicite acesso ao administrador.',
          };
        }

        initialSessionProcessedRef.current = true;
        authLoadInFlightRef.current = false;
        console.log('[AUTH:LOGIN] identity loaded');
      }

      return {};
    } catch (error) {
      console.error('[AUTH:LOGIN] exception', error);
      authLoadInFlightRef.current = false;
      return {
        error: normalizeError(error).userMessage,
      };
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithProvider = async (
    provider: 'google' | 'azure',
  ): Promise<{ error?: string }> => {
    setAuthError(null);
    const supabase = getSupabaseClient();
    if (!supabase) {
      return {
        error: normalizeError(
          new Error(
            'Supabase não configurado. Contate o administrador ou verifique as variáveis de ambiente.',
          ),
        ).userMessage,
      };
    }

    try {
      const redirectTo = `${window.location.origin}/auth/callback`;
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: { redirectTo },
      });
      if (error) {
        return { error: normalizeError(error).userMessage };
      }
      return {};
    } catch (error) {
      return {
        error: normalizeError(error).userMessage,
      };
    }
  };

  const logout = async (): Promise<void> => {
    try {
      const supabase = getSupabaseClient();
      if (supabase) {
        console.log('[AUTH] logout start');
        await supabase.auth.signOut();
        console.log('[AUTH] logout success');
      }
      clearSessionPersistence();
      setUser(null);
      setPerson(null);
      setTenantMemberships([]);
      setCurrentTenantId(null);
      setRoles([]);
      setPermissions([]);
      setRoleAssignments([]);
      setFirstLoginState(null);
      setLegalAcceptances([]);
      setIsAdminMaster(false);
      setIsCandidate(false);
      setIsEmpresa(false);
      setSession(null);
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
  };

  const changePassword = async (
    currentPassword: string,
    newPassword: string,
    _turnstileToken?: string,
  ): Promise<{ error?: string; destination?: string }> => {
    const supabase = getSupabaseClient();
    if (!supabase) {
      return {
        error: normalizeError(
          new Error(
            'Supabase não configurado. Contate o administrador ou verifique as variáveis de ambiente.',
          ),
        ).userMessage,
      };
    }

    if (!user) {
      return { error: 'Usuário não autenticado' };
    }

    try {
      if (currentPassword && user.email) {
        const { error: verifyError } = await supabase.auth.signInWithPassword({
          email: user.email,
          password: currentPassword,
        });

        if (verifyError) {
          return {
            error: 'Senha atual inválida. Verifique e tente novamente.',
          };
        }
      }

      console.log('[AUTH:CHANGE_PASSWORD] iniciando troca de senha', {
        hasSession: !!user,
        userId: user.id,
        newPasswordLength: newPassword.length,
      });

      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (updateError) {
        const updateMessage =
          updateError?.message || 'Erro ao atualizar senha. Tente novamente.';

        console.error('[AUTH:CHANGE_PASSWORD] atualização de senha falhou', {
          code: updateError?.code || null,
          message: updateMessage,
          status: updateError?.status || null,
          name: updateError?.name || null,
        });

        return {
          error: updateMessage,
        };
      }

      console.log('[AUTH:CHANGE_PASSWORD] updateUser:success');

      if (recoveryMode) {
        setRecoveryMode(false);
      }

      if (person) {
        const { error: stateError } = await supabase
          .from('first_login_state')
          .update({
            must_change_password: false,
            updated_at: new Date().toISOString(),
          })
          .eq('person_id', person.id);

        if (stateError) {
          console.error(
            '[AUTH:CHANGE_PASSWORD] first_login_state:update:failed',
            {
              code: stateError.code,
              message: stateError.message,
            },
          );
        } else {
          console.log('[AUTH:CHANGE_PASSWORD] first_login_state:updated');
        }
      }

      if (user) {
        await loadAuthData(user.id);
      }

      console.log('[AUTH:CHANGE_PASSWORD] identity:reloaded');

      const freshPersonId = person?.id || user.id;
      const [{ data: freshFirstLogin }, { data: freshLegalAcceptances }] =
        await Promise.all([
          supabase
            .from('first_login_state')
            .select('*')
            .eq('person_id', freshPersonId)
            .maybeSingle(),
          supabase
            .from('legal_acceptances')
            .select('document_type, document_version, accepted_at')
            .eq('person_id', freshPersonId)
            .order('accepted_at', { ascending: false }),
        ]);

      const mustChangePassword = freshFirstLogin?.must_change_password ?? false;
      const hasAcceptedTerms =
        (freshLegalAcceptances || []).some(
          (a: any) => a.document_type === 'terms',
        ) || freshFirstLogin?.terms_version != null;

      let destination = '/dashboard';
      if (mustChangePassword) {
        destination = '/primeiro-acesso/senha';
      } else if (!hasAcceptedTerms) {
        destination = '/auth/terms';
      } else {
        destination = '/auth/welcome';
      }

      console.log('[AUTH:CHANGE_PASSWORD] destination', {
        mustChangePassword,
        hasAcceptedTerms,
        destination,
      });

      return { destination };
    } catch (error) {
      console.error('[AUTH:CHANGE_PASSWORD] exception', error);
      return {
        error: normalizeError(error).userMessage,
      };
    }
  };

  const acceptTerms = async (
    documentType: string,
    documentVersion: string,
    metadata?: Record<string, unknown>,
  ): Promise<{ error?: string }> => {
    const supabase = getSupabaseClient();
    if (!supabase) {
      return {
        error: normalizeError(
          new Error(
            'Supabase não configurado. Contate o administrador ou verifique as variáveis de ambiente.',
          ),
        ).userMessage,
      };
    }

    if (!user || !person) {
      return { error: 'Usuário não autenticado' };
    }

    try {
      const acceptancePayload: Record<string, unknown> = {
        person_id: person.id,
        tenant_id: currentTenantId,
        document_type: documentType,
        document_version: documentVersion,
        ip: null,
        user_agent:
          typeof navigator !== 'undefined' ? navigator.userAgent : null,
        metadata: metadata || {},
      };

      const { data: acceptanceData, error: acceptanceError } = await supabase
        .from('legal_acceptances')
        .insert(acceptancePayload)
        .select('*')
        .single();

      if (acceptanceError) {
        return { error: normalizeError(acceptanceError).userMessage };
      }

      if (firstLoginState) {
        const updatePayload: Record<string, unknown> = {
          updated_at: new Date().toISOString(),
        };

        if (documentType === 'terms') {
          updatePayload.terms_version = documentVersion;
        } else if (documentType === 'privacy') {
          updatePayload.privacy_version = documentVersion;
        } else if (documentType === 'lgpd') {
          updatePayload.lgpd_consent_version = documentVersion;
        }

        const { error: stateError } = await supabase
          .from('first_login_state')
          .update(updatePayload)
          .eq('person_id', person.id);

        if (stateError) {
          console.error('[AUTH] Failed to update first_login_state:', {
            code: stateError.code,
            message: stateError.message,
            details: stateError.details,
            hint: stateError.hint,
          });
        } else {
          setFirstLoginState({
            ...firstLoginState,
            ...updatePayload,
          });
        }
      }

      if (acceptanceData) {
        setLegalAcceptances((prev) => [
          acceptanceData as LegalAcceptance,
          ...prev,
        ]);
      }

      return {};
    } catch (error) {
      return {
        error: normalizeError(error).userMessage,
      };
    }
  };

  const updateFirstLoginState = async (
    updates: Partial<FirstLoginState>,
  ): Promise<{ error?: string }> => {
    const supabase = getSupabaseClient();
    if (!supabase) {
      return {
        error: normalizeError(
          new Error(
            'Supabase não configurado. Contate o administrador ou verifique as variáveis de ambiente.',
          ),
        ).userMessage,
      };
    }

    if (!user || !person) {
      return { error: 'Usuário não autenticado' };
    }

    try {
      const payload: Record<string, unknown> = {
        ...updates,
        person_id: person.id,
        updated_at: new Date().toISOString(),
      };

      const { error: updateError } = await supabase
        .from('first_login_state')
        .upsert(payload, { onConflict: 'person_id' });

      if (updateError) {
        console.error('[FIRST_LOGIN_STATE] erro', {
          code: updateError.code,
          message: updateError.message,
          details: updateError.details,
          hint: updateError.hint,
        });
        return { error: normalizeError(updateError).userMessage };
      }

      setFirstLoginState((prev) =>
        prev
          ? { ...prev, ...updates, updated_at: new Date().toISOString() }
          : null,
      );

      return {};
    } catch (error) {
      return {
        error: normalizeError(error).userMessage,
      };
    }
  };

  const hasPermission = useCallback(
    (permissionKey: string): boolean => {
      return permissions.some((p) => p.name === permissionKey);
    },
    [permissions],
  );

  const hasAnyPermission = useCallback(
    (permissionKeys: string[]): boolean => {
      return permissionKeys.some((key) => hasPermission(key));
    },
    [hasPermission],
  );

  const hasAllPermissions = useCallback(
    (permissionKeys: string[]): boolean => {
      return permissionKeys.every((key) => hasPermission(key));
    },
    [hasPermission],
  );

  const switchTenant = useCallback(
    async (tenantId: string | null) => {
      if (!user || !person) {
        throw new Error('Faça login antes de selecionar uma organização.');
      }

      const supabase = getSupabaseClient();
      if (!supabase) {
        throw new Error('Supabase não está configurado.');
      }

      const expectedOperationId = loadOperationIdRef.current + 1;
      persistTenantSelection(user.id, null);
      setCurrentTenantId(null);
      setPermissions([]);
      setRoles([]);
      setRoleAssignments([]);
      setIsAdminMaster(false);
      setIsCandidate(false);
      setIsEmpresa(false);
      setAuthError(null);

      try {
        const result = await loadAuthData(user.id, tenantId);
        if (result.operationId !== loadOperationIdRef.current) {
          throw new Error(
            'A seleção foi substituída por outra atualização. Selecione novamente o contexto.',
          );
        }

        if (result.currentTenantId !== tenantId) {
          throw new Error(
            'Sua associação a essa organização não está mais ativa. Selecione um contexto disponível.',
          );
        }

        if (tenantId) {
          persistTenantSelection(user.id, tenantId);
        }
      } catch (error) {
        if (loadOperationIdRef.current === expectedOperationId) {
          setCurrentTenantId(null);
          persistTenantSelection(user.id, null);
        }
        throw error;
      }
    },
    [user, person, loadAuthData],
  );

  const resolvePostLoginDestination = useCallback(
    (overrideWelcomeCompleted?: boolean): string => {
      const welcomeCompleted =
        overrideWelcomeCompleted ??
        Boolean(firstLoginState?.welcome_completed_at);

      const hasAcceptedTerms =
        legalAcceptances.some((a) => a.document_type === 'terms') ||
        firstLoginState?.terms_version != null;

      const hasMembership = tenantMemberships.length > 0;
      const mustChangePassword = Boolean(firstLoginState?.must_change_password);

      const dispatchContext: DispatchContext = {
        correlationId: generateCorrelationId(),
        personId: person?.id || '',
        tenantId: currentTenantId,
        roleIds: roles.map((r) => r.id),
        permissionKeys: permissions.map((p) => p.name),
        effectivePermissions: permissions,
        isAdminMaster,
      };

      const destination = dispatcherMaster.resolveDestination(
        dispatchContext,
        welcomeCompleted,
        mustChangePassword,
        hasAcceptedTerms,
        hasMembership,
        {
          recoveryMode,
          roleNames: roles.map((r) => r.name),
          isCandidate,
        },
      );

      console.debug(
        '[AUTH:DESTINATION] DispatcherMaster resolved ->',
        destination,
      );
      return destination;
    },
    [
      currentTenantId,
      isAdminMaster,
      isCandidate,
      tenantMemberships,
      firstLoginState,
      legalAcceptances,
      permissions,
      person,
      recoveryMode,
      roleAssignments,
      roles,
    ],
  );

  const register = async (
    email: string,
    password: string,
    profileData: {
      full_name: string;
      email: string;
      phone?: string;
      tenantId?: string;
      roleId?: string;
      turnstileToken?: string;
      emailRedirectTo?: string;
      signupContext?: 'candidato' | 'empresa';
      companyName?: string;
      cnpj?: string;
    },
  ): Promise<{ error?: string; status?: 'success' | 'email_pending' }> => {
    setAuthError(null);

    const supabase = getSupabaseClient();
    if (!supabase) {
      return {
        error: normalizeError(
          new Error(
            'Supabase não configurado. Contate o administrador ou verifique as variáveis de ambiente.',
          ),
        ).userMessage,
      };
    }

    try {
      if (isTurnstileEnabled() && !profileData.turnstileToken) {
        return { error: 'Conclua a verificação de segurança.' };
      }

      if (profileData.turnstileToken) {
        const turnstileResult = await verifyTurnstileToken(
          profileData.turnstileToken,
        );
        if (!turnstileResult.ok) {
          return { error: turnstileResult.error };
        }
      }

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: profileData.full_name,
            phone: profileData.phone ?? '',
            ...(profileData.signupContext && {
              signup_context: profileData.signupContext,
              signup_origin:
                profileData.signupContext === 'empresa'
                  ? 'company_signup'
                  : 'self_signup',
            }),
            ...(profileData.companyName && {
              company_name: profileData.companyName,
            }),
            ...(profileData.cnpj && { cnpj: profileData.cnpj }),
          },
          emailRedirectTo: `${import.meta.env?.VITE_SITE_URL || window.location.origin}${profileData.emailRedirectTo || '/entrar/candidato'}`,
        },
      });

      if (error) {
        return { error: normalizeError(error).userMessage };
      }

      if (!data.user) {
        return { error: 'Erro ao criar conta. Tente novamente.' };
      }

      if (data.session) {
        await loadAuthData(data.user.id);
        return { status: 'success' };
      }

      return { status: 'email_pending' };
    } catch (error) {
      console.error('[AUTH:REGISTER] exception', error);
      return {
        error: normalizeError(error).userMessage,
      };
    }
  };

  const resetPassword = async (email: string): Promise<{ error?: string }> => {
    const supabase = getSupabaseClient();
    if (!supabase) {
      return {
        error: normalizeError(
          new Error(
            'Supabase não configurado. Contate o administrador ou verifique as variáveis de ambiente.',
          ),
        ).userMessage,
      };
    }

    try {
      console.log('[AUTH:RESET] resetPasswordForEmail start', {
        email,
        redirectTo: `${import.meta.env?.VITE_SITE_URL || window.location.origin}/redefinir-senha`,
      });
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${import.meta.env?.VITE_SITE_URL || window.location.origin}/redefinir-senha`,
      });

      console.log('[AUTH:RESET] resetPasswordForEmail result', {
        hasData: !!data,
        error: error?.message ?? null,
        code: (error as any)?.code || null,
        status: (error as any)?.status || null,
      });

      if (error) {
        return { error: normalizeError(error).userMessage };
      }

      return {};
    } catch (error) {
      console.error('[AUTH:RESET] exception', error);
      return {
        error: normalizeError(error).userMessage,
      };
    }
  };

  const updateProfile = async (
    data: Partial<Person>,
  ): Promise<{ error?: string }> => {
    const supabase = getSupabaseClient();
    if (!supabase) {
      return {
        error: normalizeError(
          new Error(
            'Supabase não configurado. Contate o administrador ou verifique as variáveis de ambiente.',
          ),
        ).userMessage,
      };
    }

    try {
      if (!user) {
        return { error: 'Usuário não autenticado' };
      }

      const updatePayload: Record<string, unknown> = {};
      if (data.full_name !== undefined)
        updatePayload.full_name = data.full_name;
      if (data.phone !== undefined) updatePayload.phone = data.phone;
      if (data.email !== undefined) updatePayload.email = data.email;

      const { error: updateError } = await supabase
        .from('people')
        .update(updatePayload)
        .eq('auth_user_id', user.id);

      if (updateError) {
        return { error: normalizeError(updateError).userMessage };
      }

      if (person) {
        setPerson({ ...person, ...data } as Person);
      }

      return {};
    } catch (error) {
      return {
        error: normalizeError(error).userMessage,
      };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        person,
        tenantMemberships,
        currentTenantId,
        tenantIds: tenantMemberships
          .map((m) => m.tenant_id)
          .filter((id): id is string => Boolean(id)),
        tenants,
        roles,
        permissions,
        roleAssignments,
        firstLoginState,
        legalAcceptances,
        isAdminMaster,
        isCandidate,
        isEmpresa,
        isAuthenticated: !!user && !!session,
        isLoading,
        login,
        loginWithProvider,
        logout,
        register,
        resetPassword,
        updateProfile,
        changePassword,
        acceptTerms,
        updateFirstLoginState,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        switchTenant,
        refreshAuthData: async (authUserId) => {
          await loadAuthData(authUserId ?? user?.id);
        },
        resolvePostLoginDestination,
        authError,
        recoveryMode,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
